import apirequests from "./indexeddb/stores/apirequests";
import retryrequests from "./indexeddb/stores/retryrequests";
import store from "../store";

export default async function (resource, options = {}, requestKey) {

	// copy the options for retrying the request - options may be modified below and this way we ensure a like-for-like retry
	const originalOptions = JSON.parse(JSON.stringify(options));

	const defaults = {
		cache: false, // cache the request?
		cacheFirst: false, // go to the cache first?
		maxCacheFirstAge: 60, // will not get from cache first if cache entry is older than this (minutes)
		maxCacheLastAge: 60 * 24, // will not get from cache last if cache entry is older than this (minutes)
		timeout: 60, // will give up on the request after this and attempt to cancel
		authed: true, // automagically attach username/authToken?
		method: 'get', // supports get or post
		data: {},
		retryIfOffline: false, // should we retry this request later if we're offline?
		ignoreauthfailed: false
	};

	options = { ...defaults, ...options }; // merge options and defaults

	// Set the method to lowercase. So we can consistently check it
	options.method = options.method.toLowerCase()

	if(options.authed) { // automagically attach username/authToken
		options.data.username = this.$UserSession.getValue("username");
		options.data.authToken = this.$UserSession.getValue("authToken");
	}

	if(localStorage.getItem('locale')){
		options.data.locale = localStorage.getItem('locale')
	} else if(this.$i18n && this.$i18n.locale){
		options.data.locale = this.$i18n.locale;
	}

	// remove any parameters that have null or undefined values
  	for (var propName in options.data) {
    	if (options.data[propName] === null || options.data[propName] === undefined) {
      		delete options.data[propName];
    	}
  	}

	// Sort out the request headers
	if(!('ContentType' in options)){
		if(options.method == 'get'){
			options.ContentType = 'application/x-www-form-urlencoded'
		} else {
			options.ContentType = 'application/json'
		}
	}
	const headers = {};
	headers['Content-Type'] = 'application/x-www-form-urlencoded';
	// headers['Content-Type'] = 'application/json';
	// headers['Referrer-Policy'] = 'origin-when-cross-origin';


	// construct the query params
	const url = new URL(resource);
	if(options.method != 'post') {
		Object.keys(options.data).forEach(key => url.searchParams.append(key, options.data[key]));
	}
	//const keys = Object.keys(options.data);
	//keys.forEach((key/*, index*/) => {
	//	if(typeof options.data[key] == 'object') {
	//		options.data[key] = JSON.stringify(options.data[key]);
	//	}
	//});




	if (options.cacheFirst) {
		// check cache
		console.log("Checking cache...");
		const result = await apirequests.getAPIRequestByURL(url.toString());
		const minTimestamp = Date.now() - options.maxCacheFirstAge * 60 * 1000; // ms
		if (result && result.timestamp > minTimestamp) { // check age of cache entry
			return result.response;
		}
		console.log("Not found in cache... Making network request");
		return makeNetworkRequest.call(this);
	}
	// make network request
	return makeNetworkRequest.call(this);


	/* ---------- ---------- ---------- ---------- ---------- */

	async function makeNetworkRequest() {

		// How to cancel a Fetch Request
		// https://davidwalsh.name/cancel-fetch
		const controller = new AbortController();
		const { signal } = controller;

		// Timeout with fetch
		// https://community.cloudflare.com/t/timeout-with-fetch/25249/4

		// Initiate the fetch but don't await it yet, just keep the promise.
		let fetchPromise;
		if(options.method == 'post') {
			fetchPromise = fetch(resource, {
				mode: 'cors',
				method: 'post',
				headers: headers,
				body: JSON.stringify(options.data),
				signal
			});
		} else {
			// assume get
			fetchPromise = fetch(url, { signal });
		}

		let response;
		if (options.timeout) {
			// Create a promise that resolves to `undefined` after x seconds.
			const timeoutPromise = new Promise((resolve) => setTimeout(resolve, options.timeout * 1000));

			// Wait for whichever promise completes first.
			try {
				response = await Promise.race([fetchPromise, timeoutPromise])
			} catch(error) {
				// Catch instant errors
  				console.log(error);
 			}
		} else {
			response = await fetchPromise;
		}

		if (response) {

			// requests made by retryRequests() (App.vue) will have a requestKey
			// delete it here so if it fails again we do not end up with multiple same requests to retry
			if(requestKey !== undefined) {
				retryrequests.deleteRequestByKey(requestKey);
			}

			if(response.status == 200){
				// success, use response
				const data = await response.json();

				// Handle login fails
				if (typeof data.status === "string" && data.status == "AUTHENTICATION_FAILED") {
					if(!options.ignoreauthfailed){
						console.log("Auth Failed");
						// modules hold their own reference to cfetch, so `this` may not be a component
						((this && this.$store) || store).commit("logOut");
						const authError = new Error("API response " + data.status);
						authError.data = data;
						throw authError;
					}
				}

				if(typeof data.status === "string" && data.status.toLowerCase() == "success"){
					if (options.cache) {
						console.log("Caching response...");
						apirequests.saveAPIRequest({
							url: url.toString(),
							data: options.data,
							response: data,
							timestamp: Date.now()
						});
					}
				}

				return data;
			} else {
				// try to read a structured error out of the body (eg. { error: true, errorMessage: '...' })
				// so callers can show something more useful than a generic failure message
				let errorData = null;
				try {
					errorData = await response.json();
				} catch (parseError) {
					// body wasn't JSON (or was empty) - errorData stays null, callers fall back to the generic message
				}
				const apiError = new Error("API response " + response.status);
				apiError.status = response.status;
				apiError.data = errorData;
				throw apiError;
			}
		} else {
			// timed out - cancel fetch request
			controller.abort();
		}


		// timed out (or instant error), handle error
		console.log("Timed out. Checking cache...");

		if(options.retryIfOffline) {
			// store to retry later
			retryrequests.saveRequest({
				url: resource,
				options: originalOptions
			});
		}

		// check cache for fallback
		const result = await apirequests.getAPIRequestByURL(url.toString(), options.data);
		const minTimestamp = Date.now() - options.maxCacheLastAge * 60 * 1000; // ms
		if (result && result.timestamp > minTimestamp) {
			return result.response;
		}
		// we've got nothing - throw an error!
		const timeoutError = new Error("timed out");
		timeoutError.timedOut = true;
		timeoutError.queuedForRetry = options.retryIfOffline;
		throw timeoutError;

	}



}
